import { BACKEND, STRONGBACKEND } from "./config";

// =====================================================
// Zentrale Fetch-Helfer – KEINE Navigation hier drin!
// Routing entscheidet über Redirects, nicht die API.
// =====================================================

async function handleUnauthorized(res: Response) {
  if (res.status === 401) {
    throw new Error("Unauthorized");
  }

  if (res.status === 403) {
    throw new Error("Forbidden");
  }
}

async function ensureJsonResponse(res: Response) {
  const ct = res.headers.get("content-type") || "";

  if (!ct.includes("application/json")) {
    const text = await res.text();
    throw new Error(
      `Expected JSON but received: ${ct}. First 200 chars: ${text.slice(0, 200)}`
    );
  }
}

// =====================================================
// JSON
// =====================================================

export async function fetchJson(url: string, options: RequestInit = {}) {
  const res = await fetch(`${BACKEND}${url}`, {
    cache: "no-store",
    credentials: "include",
    redirect: "manual",
    ...options,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
      ...(options.headers || {}),
    },
  });

  await handleUnauthorized(res);

  if (!res.ok) {
    const text = await res.text();
    throw new Error(
      `HTTP ${res.status} ${res.statusText}: ${text.slice(0, 200)}`
    );
  }

  await ensureJsonResponse(res);

  const raw = await res.text();

  try {
    return JSON.parse(raw);
  } catch {
    throw new Error(
      `JSON parse error. First 200 chars: ${raw.slice(0, 200)}`
    );
  }
}

// =====================================================
// FormData (multipart)
// =====================================================

export async function fetchFormData(url: string, formData: FormData) {
  const res = await fetch(`${BACKEND}${url}`, {
    method: "POST",
    body: formData,
    credentials: "include",
    redirect: "manual",
    headers: {
      "X-Requested-With": "XMLHttpRequest",
    },
  });

  await handleUnauthorized(res);

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`HTTP ${res.status}: ${text.slice(0, 200)}`);
  }

  // Upload-Endpunkte liefern meist JSON
  const ct = res.headers.get("content-type") || "";
  if (ct.includes("application/json")) {
    return await res.json();
  }

  return res;
}


// =====================================================
// Blob (Download via API)
// =====================================================

export async function fetchBlob(url: string): Promise<Blob> {
  const res = await fetch(`${BACKEND}${url}`, {
    credentials: "include",
    redirect: "manual",
    headers: {
      "X-Requested-With": "XMLHttpRequest",
    },
  });

  await handleUnauthorized(res);

  if (!res.ok) {
    throw new Error(`Download failed (${res.status})`);
  }

  return await res.blob();
}


// =====================================================
// Blob (starkes Backend ohne /api/v1 Prefix)
// =====================================================

export async function fetchBlobStrong(url: string): Promise<Blob> {
  const res = await fetch(`${STRONGBACKEND}${url}`, {
    credentials: "include",
    redirect: "manual",
    headers: {
      "X-Requested-With": "XMLHttpRequest",
    },
  });

  await handleUnauthorized(res);

  if (!res.ok) {
    throw new Error(`Download failed (${res.status})`);
  }

  return await res.blob();
}